import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";

const MAX_POLLS = 12;
const POLL_INTERVAL_MS = 2500;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, attempts: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, attempts: 0 });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, attempts });
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState({ status: "timeout", data, attempts });
          return;
        }
        setState({ status: "polling", data, attempts });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setState({ status: "error", data: null, attempts });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <section className="min-h-[70vh] bg-cream flex items-center justify-center px-5 py-24" data-testid="payment-success">
      <div className="max-w-lg w-full rounded-3xl border border-navy/10 bg-white p-10 text-center shadow-sm">
        {state.status === "polling" && (
          <>
            <Loader2 size={48} className="mx-auto animate-spin text-gold" />
            <h1 className="mt-6 font-display text-3xl text-navy">Confirmation du paiement…</h1>
            <p className="mt-3 text-anthracite font-body">Nous vérifions la transaction auprès de Stripe. Merci de patienter quelques secondes.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
            <h1 className="mt-6 font-display text-3xl text-navy">Paiement confirmé ✓</h1>
            <p className="mt-3 text-anthracite font-body">
              Votre paiement de <strong className="text-navy">{Number(state.data?.amount).toLocaleString("fr-FR")} €</strong> a bien été enregistré.
              Un reçu Stripe vous a été envoyé par email. Votre facture est marquée comme <strong>Payée</strong>.
            </p>
            <Link to="/espace-client" data-testid="back-to-portal" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-7 py-3 font-semibold text-navy hover:-translate-y-0.5 transition-transform">
              Retour à mon espace <ArrowRight size={16} />
            </Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <Loader2 size={48} className="mx-auto text-gold" />
            <h1 className="mt-6 font-display text-3xl text-navy">Paiement en cours de vérification</h1>
            <p className="mt-3 text-anthracite font-body">La confirmation prend plus de temps que prévu. Vous pouvez fermer cette page — votre facture sera mise à jour dès réception par Stripe.</p>
            <Link to="/espace-client" className="mt-8 inline-flex items-center gap-2 text-gold font-semibold hover:gap-3 transition-all">
              Retour à mon espace <ArrowRight size={16} />
            </Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-red-500" />
            <h1 className="mt-6 font-display text-3xl text-navy">Session introuvable</h1>
            <p className="mt-3 text-anthracite font-body">Impossible de retrouver cette session de paiement. Contactez-nous si vous pensez qu&apos;il s&apos;agit d&apos;une erreur.</p>
            <Link to="/espace-client" className="mt-8 inline-flex items-center gap-2 text-gold font-semibold">
              Retour à mon espace <ArrowRight size={16} />
            </Link>
          </>
        )}
      </div>
    </section>
  );
}
